<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="fb-question">
        <span class="fb-bar fb-bar--xl"></span>
      </div>
      <div class="fb-stage">
        <div class="fb-circle">
          <div
            v-for="(s, i) in speakers"
            :key="i"
            class="fb-avatar"
            :class="['--c-' + s.color, { '--talking': i === 0 }]"
            :style="{ '--idx': i, '--total': speakers.length }"
          >
            <span class="fb-initial">{{ s.initial }}</span>
            <span v-if="i === 0" class="fb-mic material-symbols-rounded">mic</span>
          </div>
        </div>
        <div class="fb-label">
          <span class="fb-dot"></span>
          <span class="fb-label-text">Maya is speaking</span>
        </div>
      </div>
    </template>

    <template #audience>
      <span class="fb-phone-q"></span>
      <div class="fb-phone-circle">
        <div class="fb-phone-avatar">
          M
        </div>
      </div>
      <div class="fb-phone-input">
        <span class="fb-phone-typed">What's your hot take?<span class="fb-caret">|</span></span>
      </div>
      <button type="button" class="fb-phone-ask" tabindex="-1">
        <span class="material-symbols-rounded">chat</span>
        Ask Maya
      </button>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

export default Vue.extend({
  name: 'PreviewFishbowl',
  components: { PreviewFrame },
  props: {
    slide: { type: Object as PropType<SlideType>, required: true },
  },
  data() {
    return {
      speakers: [
        { initial: 'M', color: 'pink' },
        { initial: 'J', color: 'blue' },
        { initial: 'A', color: 'green' },
        { initial: 'P', color: 'yellow' },
        { initial: 'L', color: 'pink' },
        { initial: 'R', color: 'blue' },
      ],
    }
  },
})
</script>

<style scoped lang="scss">
.fb-bar {
  display: block; height: 8px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.2);
  width: 70%;
}
.fb-question {
  padding-bottom: 6px;
  border-bottom: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}

.fb-stage {
  position: relative;
  flex: 1;
  min-height: 130px;
  display: flex; flex-direction: column; align-items: center;
  justify-content: center;
}
.fb-circle {
  position: relative;
  width: 130px; height: 130px;
  border-radius: 999px;
  border: 2px dashed color-mix(in srgb, var(--accent) 25%, transparent);
}
.fb-avatar {
  position: absolute;
  top: 50%; left: 50%;
  width: 30px; height: 30px;
  border-radius: 999px;
  display: grid; place-items: center;
  font-size: 11px; font-weight: 800;
  color: rgba(15, 15, 20, 0.7);
  /* circular layout via trigonometry approximation using transform */
  --angle: calc(var(--idx) * (360deg / var(--total)) - 90deg);
  transform: translate(-50%, -50%) rotate(var(--angle)) translate(58px) rotate(calc(var(--angle) * -1));
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(15, 15, 20, 0.15);
}
.fb-avatar.--c-pink   { background: #FFD6E7; color: #B12569; }
.fb-avatar.--c-blue   { background: #D6E8FF; color: #2B5AA8; }
.fb-avatar.--c-green  { background: #D6F3DA; color: #2A6B3A; }
.fb-avatar.--c-yellow { background: #FFF4C2; color: #8B6914; }
.fb-avatar.--talking {
  background: var(--accent); color: white;
  animation: fb-pulse 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
}
.fb-mic {
  position: absolute;
  bottom: -4px; right: -4px;
  font-size: 10px;
  background: white;
  color: var(--accent);
  border-radius: 999px;
  padding: 2px;
  border: 1.5px solid var(--accent);
}
.fb-initial { line-height: 1; }

.fb-label {
  margin-top: 6px;
  display: flex; align-items: center; gap: 5px;
}
.fb-dot {
  width: 7px; height: 7px;
  border-radius: 999px;
  background: var(--accent);
  animation: fb-blink-dot 1s ease-in-out infinite;
}
.fb-label-text {
  font-size: 10px; font-weight: 700;
  color: var(--accent);
}

/* ---- Audience phone ---- */
.fb-phone-q {
  display: block; height: 8px; border-radius: 999px;
  width: 70%; background: rgba(15, 15, 20, 0.18);
  margin-bottom: 4px;
}
.fb-phone-circle {
  display: flex; justify-content: center;
  padding: 6px 0;
}
.fb-phone-avatar {
  width: 36px; height: 36px;
  border-radius: 999px;
  background: var(--accent);
  color: white;
  display: grid; place-items: center;
  font-size: 14px; font-weight: 800;
  border: 2px solid white;
  box-shadow: 0 0 0 2px var(--accent);
  animation: fb-pulse 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
}
.fb-phone-input {
  border: 1px solid color-mix(in srgb, var(--accent) 50%, transparent);
  border-radius: 8px;
  padding: 9px 9px;
  background: white;
}
.fb-phone-typed {
  font-size: 11px; font-weight: 600;
  color: rgba(15, 15, 20, 0.8);
}
.fb-caret { color: var(--accent); animation: fb-blink 1s steps(2) infinite; }
.fb-phone-ask {
  appearance: none; border: none;
  background: var(--accent); color: white;
  font-family: inherit; font-weight: 700; font-size: 12px;
  border-radius: 8px;
  padding: 8px 12px;
  display: flex; align-items: center; justify-content: center; gap: 4px;
  cursor: pointer;
  animation: fb-tap 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
}
.fb-phone-ask .material-symbols-rounded { font-size: 14px; }

@keyframes fb-pulse {
  0%, 35%   { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 50%, transparent); }
  55%       { box-shadow: 0 0 0 8px color-mix(in srgb, var(--accent) 0%, transparent); }
  75%, 100% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 0%, transparent); }
}
@keyframes fb-blink-dot {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.3; }
}
@keyframes fb-blink { 0% { opacity: 1; } 100% { opacity: 0; } }
@keyframes fb-tap {
  0%, 60%   { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 35%, transparent); }
  72%       { box-shadow: 0 0 0 8px color-mix(in srgb, var(--accent) 0%, transparent); }
  100%      { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 0%, transparent); }
}

@media (prefers-reduced-motion: reduce) {
  .fb-avatar.--talking, .fb-phone-avatar, .fb-dot, .fb-caret, .fb-phone-ask { animation: none; }
}
</style>
